import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';

const CategoryCard = ({ category, size = 'normal' }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const name = language === 'ta' ? category.name_ta : category.name_en;
  const description = language === 'ta' ? category.description_ta : category.description_en;

  const handleClick = () => {
    navigate(`/category/${category.id}`);
  };

  return (
    <Card
      className={`group cursor-pointer overflow-hidden bg-white border border-slate-100 hover:border-blue-300 card-hover shadow-md ${
        size === 'large' ? 'col-span-2' : ''
      }`}
      onClick={handleClick}
      data-testid={`category-card-${category.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100">
        <img
          src={category.image_url}
          alt={name}
          className="w-full h-full object-cover img-zoom"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent group-hover:from-black/80 transition-all duration-500" />
        
        {/* Animated overlay pattern */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-orange-600/20"></div>
        </div>
        
        {/* Category info overlay - animated on hover */}
        <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
          <h3 className="font-bold text-xl text-white mb-1 font-['Manrope'] drop-shadow-lg">
            {name}
          </h3>
          {category.business_count > 0 && (
            <div className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full">
              <span className="text-white text-sm font-semibold">
                {category.business_count} {language === 'ta' ? 'பட்டியல்கள்' : 'listings'}
              </span>
            </div>
          )}
        </div>
        
        {/* Corner accent */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-[#E85A24] to-orange-600 opacity-0 group-hover:opacity-90 transition-opacity duration-500 rounded-bl-full"></div>
        
        {/* Shine effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        </div>
      </div>
      
      <CardContent className="p-4 text-center relative overflow-hidden">
        {/* Decorative gradient bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E85A24] via-orange-500 to-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
        
        <h3 className="font-bold text-lg bg-gradient-to-r from-[#E85A24] to-orange-600 bg-clip-text text-transparent mb-1 font-['Manrope'] group-hover:scale-105 transition-transform duration-300">
          {name}
        </h3>
        <p className="text-slate-600 text-sm group-hover:text-slate-700 transition-colors duration-300">
          {description}
        </p>
        
        {/* Animated arrow indicator */}
        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="inline-flex items-center gap-1 text-blue-600 text-sm font-medium">
            <span>View listings</span>
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoryCard;
